/**
 * WebSocket Store - Real-time vote updates
 *
 * Connects to the VoteRoom Durable Object for live voting.
 * Handles reconnection, heartbeat, and message parsing.
 */

import { signal, effect } from '@preact/signals';
import { selectedGroupId } from './groups';
import { updateSongVotes } from './songs';
import { isAuthenticated, tokens } from './auth';

// ============================================
// STATE
// ============================================

export const isConnected = signal(false);
export const connectionError = signal<string | null>(null);
export const lastPing = signal<number>(0);

// Internal state
let ws: WebSocket | null = null;
let reconnectAttempts = 0;
let reconnectTimeout: number | null = null;
let heartbeatInterval: number | null = null;

const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_DELAY_BASE = 1000; // 1 second, exponential backoff
const HEARTBEAT_INTERVAL = 30000; // 30 seconds

// ============================================
// WEBSOCKET CONNECTION
// ============================================

/**
 * Connect to the VoteRoom for a group
 */
export function connect(groupId: string): void {
  // Don't connect if not authenticated
  if (!isAuthenticated.value || !tokens.value) {
    console.log('WebSocket: Not authenticated, skipping connection');
    return;
  }

  // Disconnect existing connection
  disconnect();

  // Build WebSocket URL
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = import.meta.env.PROD
    ? window.location.host
    : 'localhost:8787';
  const wsUrl = `${protocol}//${host}/api/ws/vote-room/${groupId}?token=${tokens.value.accessToken}`;

  console.log('WebSocket: Connecting to', groupId);

  try {
    ws = new WebSocket(wsUrl);

    ws.onopen = handleOpen;
    ws.onclose = handleClose;
    ws.onerror = handleError;
    ws.onmessage = handleMessage;
  } catch (e) {
    console.error('WebSocket: Failed to create connection', e);
    connectionError.value = 'Failed to connect';
    scheduleReconnect(groupId);
  }
}

/**
 * Disconnect from WebSocket
 */
export function disconnect(): void {
  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = null;
  }

  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }

  if (ws) {
    ws.onopen = null;
    ws.onclose = null;
    ws.onerror = null;
    ws.onmessage = null;

    if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
      ws.close(1000, 'User disconnect');
    }

    ws = null;
  }

  isConnected.value = false;
  reconnectAttempts = 0;
}

// ============================================
// EVENT HANDLERS
// ============================================

function handleOpen(): void {
  console.log('WebSocket: Connected');
  isConnected.value = true;
  connectionError.value = null;
  reconnectAttempts = 0;

  // Start heartbeat to keep connection alive
  startHeartbeat();

  // Join the room
  send({ type: 'join' });
}

function handleClose(event: CloseEvent): void {
  console.log('WebSocket: Disconnected', event.code, event.reason);
  isConnected.value = false;

  stopHeartbeat();

  // Reconnect if not intentional close
  if (event.code !== 1000 && selectedGroupId.value) {
    scheduleReconnect(selectedGroupId.value);
  }
}

function handleError(event: Event): void {
  console.error('WebSocket: Error', event);
  connectionError.value = 'Connection error';
}

function handleMessage(event: MessageEvent): void {
  try {
    const data = JSON.parse(event.data);

    switch (data.type) {
      case 'vote_update':
        // Real-time vote update from another user
        handleVoteUpdate(data);
        break;

      case 'pong':
        // Heartbeat response
        lastPing.value = Date.now();
        break;

      case 'user_joined':
        // Someone joined the room (could show online count)
        console.log('WebSocket: User joined', data.userId);
        break;

      case 'user_left':
        // Someone left the room
        console.log('WebSocket: User left', data.userId);
        break;

      case 'error':
        console.error('WebSocket: Server error', data.message);
        connectionError.value = data.message;
        break;

      default:
        console.log('WebSocket: Unknown message type', data.type);
    }
  } catch (e) {
    console.error('WebSocket: Failed to parse message', e);
  }
}

/**
 * Handle real-time vote update
 */
function handleVoteUpdate(data: {
  songId: string;
  votes: {
    like: number;
    dislike: number;
    neutral: number;
    score: number;
  };
}): void {
  console.log('WebSocket: Vote update for', data.songId);
  updateSongVotes(data.songId, data.votes);
}

// ============================================
// HEARTBEAT
// ============================================

function startHeartbeat(): void {
  stopHeartbeat();

  heartbeatInterval = window.setInterval(() => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      send({ type: 'ping' });
    }
  }, HEARTBEAT_INTERVAL);
}

function stopHeartbeat(): void {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
}

// ============================================
// RECONNECTION
// ============================================

function scheduleReconnect(groupId: string): void {
  if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
    console.log('WebSocket: Max reconnect attempts reached');
    connectionError.value = 'Unable to connect. Please refresh.';
    return;
  }

  // Exponential backoff with jitter
  const delay = RECONNECT_DELAY_BASE * Math.pow(2, reconnectAttempts) + Math.random() * 1000;
  reconnectAttempts++;

  console.log(`WebSocket: Reconnecting in ${Math.round(delay)}ms (attempt ${reconnectAttempts})`);

  reconnectTimeout = window.setTimeout(() => {
    if (selectedGroupId.value === groupId) {
      connect(groupId);
    }
  }, delay);
}

// ============================================
// SEND MESSAGES
// ============================================

/**
 * Send a message over WebSocket
 */
export function send(message: Record<string, unknown>): void {
  if (!ws || ws.readyState !== WebSocket.OPEN) {
    console.warn('WebSocket: Cannot send, not connected');
    return;
  }

  ws.send(JSON.stringify(message));
}

/**
 * Broadcast a vote (called after successful API vote)
 */
export function broadcastVote(songId: string, voteType: string): void {
  send({
    type: 'vote',
    songId,
    voteType,
  });
}

// ============================================
// AUTO-CONNECT ON GROUP CHANGE
// ============================================

// Connect when group changes (and authenticated)
effect(() => {
  const groupId = selectedGroupId.value;
  const authenticated = isAuthenticated.value;

  if (groupId && authenticated) {
    connect(groupId);
  } else {
    disconnect();
  }
});

// Disconnect on logout
effect(() => {
  if (!isAuthenticated.value) {
    disconnect();
  }
});

// Reconnect on visibility change (tab becomes visible)
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && selectedGroupId.value && !isConnected.value) {
      reconnectAttempts = 0; // Reset attempts when user returns
      connect(selectedGroupId.value);
    }
  });
}
